/**
 * Client-side helpers for the AI Agents admin dashboard tab.
 * Uses Firebase v9 modular SDK with onSnapshot for real-time updates.
 */

import { functions } from '../firebase';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

// ============================================================
// Agent ↔ Dashboard Tab mapping
// ============================================================

export const AGENT_TAB_MAPPING = {
  'security-agent': ['Pending Approvals', 'All Users'],
  'payment-agent': ['Client Management'],
  'monitoring-agent': ['Overview'],
  'marketing-agent': ['Content Management', 'LinkedIn Integration'],
  'onboarding-agent': ['Client Management', 'Pending Approvals'],
  'directory-scraping-agent': ['Agency Pipeline'],
  'website-qa-agent': [],
  'new-business-filing-agent': ['Agency Pipeline'],
  'contact-enrichment-agent': ['Agency Pipeline', 'LinkedIn Integration'],
};

// ============================================================
// Real-time subscriptions
// ============================================================

export function subscribeToAgentRegistry(callback) {
  const q = query(collection(db, 'agent_registry'));
  return onSnapshot(q, (snapshot) => {
    const agents = snapshot.docs.map((d) => ({id: d.id, ...d.data()}));
    callback(agents);
  });
}

export function subscribeToAgentAlerts(callback, { unreadOnly = false, limit: maxResults = 50 } = {}) {
  let q;
  if (unreadOnly) {
    q = query(
      collection(db, 'agent_alerts'),
      where('read', '==', false),
      orderBy('createdAt', 'desc'),
      firestoreLimit(maxResults)
    );
  } else {
    q = query(
      collection(db, 'agent_alerts'),
      orderBy('createdAt', 'desc'),
      firestoreLimit(maxResults)
    );
  }
  return onSnapshot(q, (snapshot) => {
    const alerts = snapshot.docs.map((d) => ({id: d.id, ...d.data()}));
    callback(alerts);
  });
}

export function subscribeToAgentEvents(callback, { agentId = null, severity = null, since = null, limit: maxResults = 100 } = {}) {
  let constraints = [orderBy('timestamp', 'desc'), firestoreLimit(maxResults)];

  if (agentId) {
    constraints = [where('agentId', '==', agentId), ...constraints];
  }
  if (severity) {
    constraints = [where('severity', '==', severity), ...constraints];
  }
  if (since) {
    constraints = [where('timestamp', '>=', Timestamp.fromDate(since)), ...constraints];
  }

  const q = query(collection(db, 'agent_events'), ...constraints);
  return onSnapshot(q, (snapshot) => {
    const events = snapshot.docs.map((d) => ({id: d.id, ...d.data()}));
    callback(events);
  });
}

export function subscribeToAgentActions(callback, { agentId = null, limit: maxResults = 50 } = {}) {
  let constraints = [orderBy('timestamp', 'desc'), firestoreLimit(maxResults)];
  if (agentId) {
    constraints = [where('agentId', '==', agentId), ...constraints];
  }
  const q = query(collection(db, 'agent_actions'), ...constraints);
  return onSnapshot(q, (snapshot) => {
    const actions = snapshot.docs.map((d) => ({id: d.id, ...d.data()}));
    callback(actions);
  });
}

// ============================================================
// Write operations
// ============================================================

export async function markAlertAsRead(alertId) {
  await updateDoc(doc(db, 'agent_alerts', alertId), {
    read: true,
    readBy: 'admin',
    readAt: serverTimestamp(),
  });
}

export async function markEventResolved(eventId) {
  await updateDoc(doc(db, 'agent_events', eventId), {
    resolved: true,
    resolvedBy: 'admin',
    resolvedAt: serverTimestamp(),
  });
}

export async function toggleAgent(agentId, enabled) {
  await updateDoc(doc(db, 'agent_registry', agentId), {
    enabled,
  });
}
